/**
 * Marketplace — Browse, install, publish, and rate OpenClaw personality templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../auth';
import './MarketplacePage.css';

const CATEGORY_ICONS = {
  'general': '🤖', 'real-estate': '🏠', 'medical': '🏥', 'legal': '⚖️',
  'restaurant': '🍽️', 'retail': '🛍️', 'fitness': '🏋️', 'automotive': '🚗',
  'tech-support': '💻', 'salon-spa': '💇', 'finance': '💰', 'education': '📚',
  'travel': '✈️', 'nonprofit': '❤️', 'custom': '⚙️'
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [sort, setSort] = useState('downloads');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showPublish, setShowPublish] = useState(false);
  const [installing, setInstalling] = useState(null);
  const [ratingPrompt, setRatingPrompt] = useState(null);

  const loadPrompts = useCallback(async () => {
    try {
      let url = `/api/marketplace/prompts?sort=${sort}`;
      if (activeCategory !== 'all') url += `&category=${activeCategory}`;
      if (search) url += `&q=${encodeURIComponent(search)}`;
      const res = await authFetch(url);
      if (res.ok) setPrompts(await res.json());
    } catch (e) {} finally { setLoading(false); }
  }, [sort, activeCategory, search]);

  useEffect(() => {
    authFetch('/api/marketplace/categories').then(r => r.json()).then(setCategories).catch(() => {});
    loadPrompts();
  }, [loadPrompts]);

  const installPrompt = async (id) => {
    setInstalling(id);
    try {
      const res = await authFetch(`/api/marketplace/prompts/${id}/install`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setPrompts(prev => prev.map(p => p.id === id ? { ...p, downloads: (p.downloads || 0) + 1 } : p));
        // Navigate to AI Assistant settings with template pre-filled
        const template = selected || prompts.find(p => p.id === id);
        setSelected(null);
        navigate('/ai-agent', { state: { applyTemplate: template } });
      } else {
        alert(data.error || 'Install failed');
      }
    } catch (e) { alert('Install failed'); }
    finally { setInstalling(null); }
  };

  const submitRating = async (id, score, review) => {
    await authFetch(`/api/marketplace/prompts/${id}/rate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score, review })
    });
    setRatingPrompt(null);
    loadPrompts();
  };

  const loadDetail = async (id) => {
    const res = await authFetch(`/api/marketplace/prompts/${id}`);
    if (res.ok) setSelected(await res.json());
  };

  return (
    <div className="marketplace-page">
      <div className="mp-header">
        <h2>Marketplace</h2>
        <p className="mp-subtitle">Personality templates for your AI Assistant</p>
        <button className="btn-publish" onClick={() => setShowPublish(true)}>+ Publish Your Own</button>
      </div>

      <div className="mp-categories">
        <button className={`cat-btn ${activeCategory === 'all' ? 'active' : ''}`} onClick={() => setActiveCategory('all')}>All</button>
        {categories.map(c => (
          <button key={c} className={`cat-btn ${activeCategory === c ? 'active' : ''}`} onClick={() => setActiveCategory(c)}>
            {CATEGORY_ICONS[c] || '📦'} {c.replace(/-/g, ' ')}
          </button>
        ))}
      </div>

      <div className="mp-controls">
        <select value={sort} onChange={e => setSort(e.target.value)}>
          <option value="downloads">Most Popular</option>
          <option value="rating">Top Rated</option>
          <option value="newest">Newest</option>
        </select>
        <div className="mp-search">
          <input type="text" placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? <div className="mp-loading">Loading...</div> : (
        <div className="mp-grid">
          {prompts.map(p => (
            <div key={p.id} className="mp-card" onClick={() => loadDetail(p.id)}>
              <div className="mp-card-icon">{CATEGORY_ICONS[p.category] || '📦'}</div>
              <h3>{p.name}</h3>
              <p className="mp-card-desc">{p.description.length > 80 ? p.description.substring(0, 80) + '...' : p.description}</p>
              <div className="mp-card-meta">
                <span className="mp-stars">{'⭐'.repeat(Math.round(p.avgRating || 0))}{p.ratingCount ? ` (${p.ratingCount})` : ''}</span>
                <span className="mp-downloads">{p.downloads || 0} installs</span>
              </div>
              <div className="mp-card-footer">
                <span className="mp-card-cat">{p.category}</span>
                <span className="mp-card-author">by {p.authorName}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {prompts.length === 0 && !loading && <div className="mp-empty">No templates found. Try a different category or search.</div>}

      {/* Detail Modal */}
      {selected && (
        <div className="mp-modal-overlay" onClick={() => setSelected(null)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header">
              <div className="mp-modal-icon">{CATEGORY_ICONS[selected.category] || '📦'}</div>
              <div>
                <h3>{selected.name}</h3>
                <span className="mp-modal-author">by {selected.authorName}</span>
                <span className="mp-modal-stars">{'⭐'.repeat(Math.round(selected.avgRating || 0))} {selected.ratingCount ? `(${selected.ratingCount} reviews)` : 'No reviews yet'}</span>
              </div>
              <button className="btn-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="mp-modal-body">
              <p className="mp-modal-desc">{selected.description}</p>

              <div className="mp-preview">
                <div className="mp-preview-item"><strong>Greeting:</strong> "{selected.greeting}"</div>
                <div className="mp-preview-item"><strong>Voice:</strong> {selected.voiceId}</div>
                <div className="mp-preview-item"><strong>Category:</strong> {selected.category}</div>
                {selected.tags && selected.tags.length > 0 && (
                  <div className="mp-preview-item"><strong>Tags:</strong> {selected.tags.join(', ')}</div>
                )}
              </div>

              {selected.soul && (
                <div className="mp-section">
                  <h4>Personality</h4>
                  <p className="mp-soul">{selected.soul}</p>
                </div>
              )}

              {selected.ratings && selected.ratings.length > 0 && (
                <div className="mp-section">
                  <h4>Reviews</h4>
                  {selected.ratings.slice(0, 5).map((r, i) => (
                    <div key={i} className="mp-review">
                      <span className="mp-review-stars">{'⭐'.repeat(r.score)}</span>
                      <span className="mp-review-text">{r.review || 'No comment'}</span>
                      <span className="mp-review-author">{r.email.split('@')[0]}</span>
                    </div>
                  ))}
                </div>
              )}

              {selected.status === 'pending' && (
                <div className="mp-pending-badge">Pending moderation — not yet visible to others</div>
              )}
            </div>
            <div className="mp-modal-footer">
              <button className="btn-rate" onClick={() => setRatingPrompt(selected.id)}>⭐ Rate</button>
              <button className="btn-install" onClick={() => installPrompt(selected.id)} disabled={installing === selected.id}>
                {installing === selected.id ? 'Installing...' : 'Install to my Agent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {ratingPrompt && <RatingModal promptId={ratingPrompt} onSubmit={submitRating} onClose={() => setRatingPrompt(null)} />}

      {/* Publish Modal */}
      {showPublish && <PublishModal categories={categories} onClose={() => setShowPublish(false)} onPublished={() => { setShowPublish(false); loadPrompts(); }} />}
    </div>
  );
};

const RatingModal = ({ promptId, onSubmit, onClose }) => {
  const [score, setScore] = useState(5);
  const [review, setReview] = useState('');
  return (
    <div className="mp-modal-overlay mp-modal-overlay-top" onClick={onClose}>
      <div className="mp-modal mp-modal-sm" onClick={e => e.stopPropagation()}>
        <h3>Rate this template</h3>
        <div className="rating-stars">
          {[1,2,3,4,5].map(s => (
            <button key={s} className={`star-btn ${s <= score ? 'active' : ''}`} onClick={() => setScore(s)}>⭐</button>
          ))}
        </div>
        <textarea placeholder="Write a review (optional)..." value={review} onChange={e => setReview(e.target.value)} rows={3} />
        <div className="mp-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-submit-rating" onClick={() => onSubmit(promptId, score, review)}>Submit</button>
        </div>
      </div>
    </div>
  );
};

const PublishModal = ({ categories, onClose, onPublished }) => {
  const [form, setForm] = useState({
    name: '', description: '', category: 'general', tags: '',
    soul: '', systemPrompt: '', greeting: '', voiceId: 'sarah',
    instructions: '', rules: ''
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name || !form.description) return alert('Name and description required');
    setSaving(true);
    try {
      const res = await authFetch('/api/marketplace/prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('Published! Your template is pending moderation and will be visible once approved.');
        onPublished();
      } else {
        alert(data.error || 'Publish failed');
      }
    } catch (e) { alert('Publish failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-modal mp-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>Publish a Template</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className="mp-modal-body mp-publish-form">
          <div className="mp-form-row">
            <div className="mp-field">
              <label>Template Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dental Office Assistant" />
            </div>
            <div className="mp-field">
              <label>Category *</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {categories.map(c => <option key={c} value={c}>{c.replace(/-/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="mp-field">
            <label>Description *</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What does this template do? Who is it for?" />
          </div>
          <div className="mp-field">
            <label>Personality (Soul)</label>
            <textarea value={form.soul} onChange={e => setForm({ ...form, soul: e.target.value })} rows={3} placeholder="Describe the personality traits, communication style, and core values..." />
          </div>
          <div className="mp-field">
            <label>System Prompt</label>
            <textarea value={form.systemPrompt} onChange={e => setForm({ ...form, systemPrompt: e.target.value })} rows={4} placeholder="Detailed instructions for how the AI should behave, what tools to use, how to handle calls..." />
          </div>
          <div className="mp-form-row">
            <div className="mp-field">
              <label>Greeting</label>
              <input value={form.greeting} onChange={e => setForm({ ...form, greeting: e.target.value })} placeholder="What to say when answering" />
            </div>
            <div className="mp-field">
              <label>Voice</label>
              <select value={form.voiceId} onChange={e => setForm({ ...form, voiceId: e.target.value })}>
                <option value="sarah">Sarah (Warm)</option>
                <option value="james">James (Professional)</option>
                <option value="sofia">Sofia (Friendly)</option>
                <option value="george">George (British)</option>
                <option value="lily">Lily (Energetic)</option>
              </select>
            </div>
          </div>
          <div className="mp-field">
            <label>Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="scheduling, appointments, medical" />
          </div>
          <div className="mp-moderation-note">
            Your template will be reviewed before being published to the marketplace. This usually takes less than 24 hours.
          </div>
        </div>
        <div className="mp-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-publish-submit" onClick={submit} disabled={saving}>{saving ? 'Publishing...' : 'Submit for Review'}</button>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
